import { useState } from "react";
import { Loader2, Sparkles } from "lucide-react";
import { Modal } from "../../components/ui/modal";
import { cn } from "../../lib/utils";
import {
  MIN_REASON_LENGTH,
  allReasonsGiven,
  buildOverrides,
  findingKey,
  groupByField,
  oldFormatFields,
  splitFindings,
  type OutcomeFinding,
  type OutcomeOverride,
  type OutcomeValidation,
} from "./outcome-validation";

function tone(finding: OutcomeFinding) {
  if (finding.blocking) return { label: "Must fix", dot: "bg-danger", text: "text-danger" };
  if (finding.existing) return { label: "Old issue", dot: "bg-ink-3", text: "text-ink-3" };
  if (finding.source === "llm") return { label: "AI suggestion", dot: "bg-brand", text: "text-brand" };
  return { label: "Suggestion", dot: "bg-warning", text: "text-warning" };
}

const IMPACT_TEXT: Record<string, string> = {
  "changes stored values": "Fixing this changes how calls are stored.",
  "breaks a reference": "Something else uses this value.",
};

// One finding, short: what is wrong and what to do. Rule ids stay out of sight (hover for it).
export function FindingCard({ finding }: { finding: OutcomeFinding }) {
  const { label, dot, text } = tone(finding);
  const showQuote = finding.quote && finding.quote.length > 12 && !finding.message.includes(finding.quote);
  return (
    <li className="flex gap-2 text-xs leading-5" data-testid="outcome-finding" title={finding.rule_id}>
      <span className={cn("mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full", dot)} />
      <div className="min-w-0">
        <p className="text-ink"><span className={cn("font-bold", text)}>{label}: </span>{finding.message}</p>
        {showQuote && <p className="truncate italic text-ink-3">"{finding.quote}"</p>}
        {finding.suggestion && <p className="text-ink-2">{finding.suggestion}</p>}
        {IMPACT_TEXT[finding.impact] && <p className="text-[11px] text-ink-3">{IMPACT_TEXT[finding.impact]}</p>}
      </div>
    </li>
  );
}

// Findings for one outcome, shown under it, and only there. Old issues appear only when the author asks.
export function InlineFindings({ findings, showOld = false }: { findings: OutcomeFinding[]; showOld?: boolean }) {
  const { blocking, suggestions, existing } = splitFindings(findings);
  const shown = [...blocking, ...suggestions, ...(showOld ? existing : [])];
  if (!shown.length) return null;
  return (
    <ul className="mt-3 grid gap-1.5 rounded-md border border-line bg-surface px-3 py-2" data-testid="outcome-inline-findings">
      {shown.map((f, i) => <FindingCard key={`${f.rule_id}-${i}`} finding={f} />)}
    </ul>
  );
}

// A one-line summary above the outcomes. The details are under each outcome, so nothing is listed twice.
export function OutcomeFindingsPanel({
  validation,
  onReviewWithAi,
  reviewing,
  showOld = false,
  onShowOldChange,
}: {
  validation: OutcomeValidation | null;
  onReviewWithAi?: () => void;
  reviewing?: boolean;
  showOld?: boolean;
  onShowOldChange?: (next: boolean) => void;
}) {
  if (!validation || validation.mode === "off") return null;
  const { blocking, suggestions, existing } = splitFindings(validation.findings);
  const oldFormat = oldFormatFields(validation.findings);
  const parts = [
    blocking.length ? `${blocking.length} must fix` : "",
    suggestions.length ? `${suggestions.length} suggestion${suggestions.length === 1 ? "" : "s"}` : "",
  ].filter(Boolean);
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-line bg-surface px-3 py-2 text-xs" data-testid="outcome-findings-panel">
      <span className={cn("font-bold", blocking.length ? "text-danger" : suggestions.length ? "text-warning" : "text-success")}>
        {parts.length ? parts.join(" · ") : "No problems in new or edited outcomes"}
      </span>
      {oldFormat.length > 0 && (
        <span className="text-ink-3">{oldFormat.length} in old text format</span>
      )}
      {existing.length > 0 && onShowOldChange && (
        <button type="button" onClick={() => onShowOldChange(!showOld)} className="font-bold text-ink-3 hover:text-ink">
          {showOld ? "Hide old issues" : `Show old issues (${existing.length})`}
        </button>
      )}
      {validation.llm.enabled && onReviewWithAi && (
        <button
          type="button"
          onClick={onReviewWithAi}
          disabled={reviewing}
          className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-line bg-elevated px-2.5 py-1 font-bold text-ink hover:bg-soft disabled:opacity-50"
        >
          {reviewing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
          Review with AI
        </button>
      )}
      {validation.llm.error && <span className="w-full text-warning">AI review could not run: {validation.llm.error}</span>}
    </div>
  );
}

// Shown when a save would be stopped. The author fixes the problems, or accepts each one with a reason.
export function OutcomeBlockModal({
  findings,
  busy,
  onClose,
  onContinue,
}: {
  findings: OutcomeFinding[];
  busy?: boolean;
  onClose: () => void;
  onContinue: (overrides: OutcomeOverride[]) => void;
}) {
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const { blocking, suggestions } = splitFindings(findings);
  const ready = blocking.length > 0 && allReasonsGiven(blocking, reasons);
  return (
    <Modal
      title="Some outcomes need changes"
      subtitle="These would break the allowed values, so the save is stopped. Fix them, or accept each one with a reason."
      onClose={() => { if (!busy) onClose(); }}
      width="max-w-3xl"
      footer={(
        <>
          <button type="button" onClick={onClose} disabled={busy} className="rounded-md border border-line bg-elevated px-4 py-2 text-xs font-bold text-ink hover:bg-soft disabled:opacity-50">
            Go back and fix
          </button>
          <button
            type="button"
            disabled={!ready || busy}
            onClick={() => onContinue(buildOverrides(blocking, reasons))}
            className="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-xs font-bold text-white hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-40"
          >
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Publish with these reasons
          </button>
        </>
      )}
    >
      <div className="grid gap-4" data-testid="outcome-block-modal">
        {groupByField(blocking).map(([field, items]) => (
          <div key={field}>
            <p className="mb-2 text-xs font-bold text-ink">{field || "(unnamed outcome)"}</p>
            <ul className="grid gap-3">
              {items.map((finding) => {
                const key = findingKey(finding);
                return (
                  <li key={key} className="grid gap-2">
                    <ul><FindingCard finding={finding} /></ul>
                    <label className="block">
                      <span className="mb-1 block text-[11px] font-bold text-ink-2">Why is this OK? (at least {MIN_REASON_LENGTH} characters, kept in the log)</span>
                      <input
                        value={reasons[key] || ""}
                        maxLength={500}
                        onChange={(event) => setReasons((current) => ({ ...current, [key]: event.target.value }))}
                        className="h-9 w-full rounded-md border border-line bg-elevated px-3 text-sm outline-none focus:border-brand"
                      />
                    </label>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
        {suggestions.length > 0 && (
          <p className="text-[11px] text-ink-3">{suggestions.length} more suggestions do not stop the save. They are listed in the outcome check.</p>
        )}
      </div>
    </Modal>
  );
}
